// CIFP — Coded Instrument Flight Procedures parser.
//
// Reads FAA CIFP (ARINC 424.18) fixed-width records and emits typed
// JS objects. CIFP is a 397k-line text file delivered by the FAA on
// a 28-day cycle. Each line is a fixed-width record describing one
// airport, navaid, fix, airway segment, SID/STAR/IAP leg, etc.
//
// We only consume the subset Fyrefly cares about:
//   - SUSAP <airport>K? D|E|F ...   → SID / STAR / IAP procedure legs
//   - SUSAP <airport>K? C <ident> ... → terminal waypoint (lat/lon)
//   - SUSAEAENRT  <ident> ...        → enroute fix
//   - SUSAD <ident> ...               → VOR / VORTAC / VOR-DME navaid
//   - SUSADB <ident> ...              → NDB / NDB-DME navaid
//
// Each procedure record (D/E/F) names a fix by ident. The lat/lon of
// that fix lives in a SEPARATE record (one of PC / EA / D / DB). The
// parser builds a lookup table on first pass and joins on second pass.
//
// Path-and-terminator codes ("leg types"):
//   IF — initial fix (just a fix; no path)
//   TF — track to a fix (great-circle from previous fix)
//   DF — direct to a fix (from current position straight to fix)
//   CF — course to a fix
//   RF — constant-radius arc to a fix (RNP)
//   VA — heading to altitude (no terminating fix)
//   CA — course to altitude (no terminating fix)
//   FA — fix to altitude
//   FC — fix to distance (track from fix for N nm)
//   FD — fix to DME distance
//   CR — course to radial (intercept)
//   CI — course to intercept
//   CD — course to DME distance
//   PI — procedure turn
//   HM, HF, HA — holding patterns (manual/fix-end/altitude-end)
//   AF — arc to fix
//   VI, VM — heading to intercept / manual
//   VR — heading to radial
//   VD — heading to DME distance
//
// For map rendering we group leg types into three classes:
//   - 'positional': leg ends at a known lat/lon → render normally (IF, TF, DF, CF, RF, FA, AF, HF, HM, HA)
//   - 'altitude-terminator': leg has no fix; we can't precisely render
//     the path without performance data → render dashed + degrade gracefully
//   - 'manual-terminator': pilot input required (VI, VM) → degrade gracefully
//
// Useful references:
//   FAA CIFP product page  https://www.faa.gov/air_traffic/flight_info/aeronav/digital_products/cifp/
//   ARINC 424 spec (paywalled — we work from the data)
//   Reference parsers: jack-laverty/arinc424 (Python), jojo2357/flightgear-star-sid-manager (JS)

// ── Packed lat/lon decoder ───────────────────────────────────────────────
//
// ARINC 424 packs lat and lon into ASCII strings:
//   Lat (9 chars):  H DD MM SS hh   → N37361772 = N 37° 36' 17.72"
//   Lon (10 chars): H DDD MM SS hh  → W122201180 = W 122° 20' 11.80"
// Trailing two digits are hundredths of a second.

const DEG_PER_SEC = 1 / 3600;

export function decodeLat(packed: string): number | null {
  if (packed.length < 9) return null;
  const hemi = packed[0];
  if (hemi !== 'N' && hemi !== 'S') return null;
  const dd = Number(packed.substring(1, 3));
  const mm = Number(packed.substring(3, 5));
  const ss = Number(packed.substring(5, 7));
  const hh = Number(packed.substring(7, 9));
  if (!Number.isFinite(dd) || !Number.isFinite(mm) || !Number.isFinite(ss) || !Number.isFinite(hh)) return null;
  const sign = hemi === 'N' ? 1 : -1;
  return sign * (dd + mm / 60 + (ss + hh / 100) * DEG_PER_SEC);
}

export function decodeLon(packed: string): number | null {
  if (packed.length < 10) return null;
  const hemi = packed[0];
  if (hemi !== 'E' && hemi !== 'W') return null;
  const ddd = Number(packed.substring(1, 4));
  const mm = Number(packed.substring(4, 6));
  const ss = Number(packed.substring(6, 8));
  const hh = Number(packed.substring(8, 10));
  if (!Number.isFinite(ddd) || !Number.isFinite(mm) || !Number.isFinite(ss) || !Number.isFinite(hh)) return null;
  const sign = hemi === 'E' ? 1 : -1;
  return sign * (ddd + mm / 60 + (ss + hh / 100) * DEG_PER_SEC);
}

// ── Record types ─────────────────────────────────────────────────────────

export type RouteType = 'SID' | 'STAR' | 'IAP';

export type LegType =
  | 'IF' | 'TF' | 'DF' | 'CF' | 'RF' | 'AF'
  | 'VA' | 'CA' | 'FA'
  | 'FC' | 'FD' | 'CD'
  | 'CR' | 'CI' | 'VI' | 'VM' | 'VR' | 'VD'
  | 'PI'
  | 'HM' | 'HF' | 'HA'
  | 'UNKNOWN';

/** Classes that determine map rendering strategy. */
export type LegClass = 'positional' | 'altitude-terminator' | 'manual-terminator' | 'special';

const POSITIONAL: ReadonlySet<LegType> = new Set(['IF', 'TF', 'DF', 'CF', 'RF', 'AF', 'HF', 'HA', 'HM', 'FD', 'FC']);
const ALT_TERM:   ReadonlySet<LegType> = new Set(['VA', 'CA', 'FA']);
const MAN_TERM:   ReadonlySet<LegType> = new Set(['VI', 'VM', 'CI']);
const SPECIAL:    ReadonlySet<LegType> = new Set(['CR', 'VR', 'CD', 'PI']);

export function classifyLeg(t: LegType): LegClass {
  if (POSITIONAL.has(t)) return 'positional';
  if (ALT_TERM.has(t)) return 'altitude-terminator';
  if (MAN_TERM.has(t)) return 'manual-terminator';
  if (SPECIAL.has(t)) return 'special';
  return 'manual-terminator'; // unknown — degrade safely
}

/** A single leg of a procedure as emitted by the parser. The fix
 *  lat/lon are joined in from the fix lookup table. */
export interface ProcedureLeg {
  /** Sequence number within the procedure (010, 020, ...). */
  seq: number;
  /** Path-and-terminator code (TF, DF, CF, ...). */
  legType: LegType;
  /** Named fix ident (5 chars, trimmed) — may be empty for VA/CA legs. */
  fixIdent: string;
  /** Fix lat/lon, populated by the fix-table join. Null when no fix
   *  (altitude-terminator legs) or when the fix is missing from the
   *  CIFP fix tables. */
  fixLat: number | null;
  fixLon: number | null;
  /** Altitude constraint 1 (feet MSL or 'FL250' format). Null when absent. */
  altitude1: string | null;
  /** Altitude constraint 2. Null when absent. */
  altitude2: string | null;
  /** Speed constraint (kt). Null when absent. */
  speedLimit: number | null;
  /** Recommended NAVAID ident for this leg (e.g. SFO for a CF). Null when absent. */
  recNavaid: string | null;
  /** Turn direction: 'L' / 'R' / null. */
  turnDirection: 'L' | 'R' | null;
  /** True when this leg is part of an IAP's missed-approach segment
   *  (the MAP itself + everything after). Decoded from the CIFP
   *  waypoint-description-code at column 42-43. */
  isMissedApproach: boolean;
  /** True when this leg's terminator is the Final Approach Fix (FAF).
   *  Decoded from CIFP waypoint description code col 41 = 'F'.
   *  Rendered with a Maltese-cross symbol on the map. */
  isFaf: boolean;
  /** True when this leg's terminator is the Missed Approach Point
   *  (col 43 = 'M'). The MAP is the runway threshold for ILS/LOC,
   *  the runway end or a named MAP fix for RNAV. */
  isMap: boolean;
  /** True when this leg's terminator is the Initial Approach Fix (IAF)
   *  on an IAP. Decoded from col 41 = 'A' or col 42 = 'A'. */
  isIaf: boolean;
  /** True when this leg's terminator is an Intermediate Fix on the
   *  approach (col 43 = 'I'). Different from CIFP leg-type 'IF' which
   *  is 'initial fix' = start of a sequence. */
  isIntermediateFix: boolean;
  /** True when this leg's terminator is a stepdown fix on the
   *  final approach (col 42 = 'S'). Has its own altitude constraint
   *  and is between the FAF and MAP. */
  isStepdownFix: boolean;
  /** True when this leg's terminator is a fly-over fix (col 42 = 'Y')
   *  — must be flown over before turning, vs the default fly-by where
   *  the turn anticipates the fix. */
  isFlyOver: boolean;
  /** Altitude constraint type from col 83. Values:
   *   '+'           — at or above altitude1
   *   '-'           — at or below altitude1
   *   'B'           — between altitude1 (high) and altitude2 (low)
   *   'at'          — at altitude1 exactly (no prefix char)
   *   'recommended' — altitude1 is advisory only ('*')
   *   null          — no altitude constraint published. */
  altitudeConstraint: '+' | '-' | 'B' | 'at' | 'recommended' | null;
  /** True when this leg is a holding pattern in lieu of procedure turn
   *  or hold-in-lieu-of-PT. Decoded from waypoint desc code. */
  isHoldingPattern: boolean;
  /** Inbound course at the holding fix in degrees magnetic. Only set
   *  on HM/HF/HA legs (and a few procedure-turn legs). Decoded from
   *  CIFP columns 71-74. */
  holdInboundCourse: number | null;
  /** Outbound leg length on a holding pattern: time- or distance-based.
   *  null when not specified or not a holding leg. */
  holdLeg: { kind: 'time'; minutes: number } | { kind: 'distance'; nm: number } | null;
  /** Center fix ident for RF (radius-to-fix) legs. The radius is
   *  computed from the great-circle distance between the leg start
   *  point and this center, so we don't need to store it separately. */
  rfCenterFix: string | null;
  /** Resolved center-fix coordinates (joined at parse time). */
  rfCenterLat: number | null;
  rfCenterLon: number | null;
  /** Magnetic course/heading published for the leg (degrees). Used by
   *  altitude-terminator synthesis (VA/CA/FA) and CF intercepts.
   *  Decoded from cols 71-74. */
  magCourse: number | null;
}

export interface Procedure {
  /** Airport this procedure belongs to (4-char ICAO). */
  airportIcao: string;
  /** SID / STAR / IAP. */
  routeType: RouteType;
  /** Procedure ident (e.g. 'CIITY3', 'ALWYS3', 'H10RZ'). */
  procIdent: string;
  /** Procedure name = procIdent for now (CIFP doesn't carry a long name in the same record). */
  /** Transitions (route ident) inside this procedure. */
  transitions: Transition[];
}

export interface Transition {
  /** Transition ident:
   *  - SID:   '4RW10L' (runway transition) or named (e.g. 'ALAMO')
   *  - STAR:  '4INYOE' (transition fix) or runway-end ident
   *  - IAP:   blank/`H` (common segment) or runway-end ident */
  ident: string;
  /** Whether this is the runway segment, transition, or common. */
  kind: 'runway' | 'transition' | 'common';
  /** Ordered legs from first to last. */
  legs: ProcedureLeg[];
}

// ── Column constants (1-indexed in the spec; we'll slice 0-indexed) ─────
//
// SUSAP <icao(4)> <icao-region(2)> <route-type(1)> <proc-ident(6)>
// <trans-ident(5)> <continuation(2,blank or 0)> <seq(3)>
// <fix-ident(5)> <fix-region(2)> <fix-sect-subsect(2)>
// <continuation-rec(1)> <waypoint-desc(4)>
// ...path-and-terminator at col 47-48 (0-indexed 46-48 exclusive)
// (we'll verify slice positions against the data on every test run.)

interface ParsedHeader {
  /** Column 1 (0-indexed 0) sentinel — must be 'S'. */
  ok: boolean;
}

function isAirportProcedure(line: string): boolean {
  // 'SUSAP ' followed by 4-char icao, then K?, then D/E/F
  if (line.length < 13) return false;
  if (line[0] !== 'S' || line[5] !== ' ' && line[5] !== 'P') return false;
  // section 'P' at col 5 (0-indexed) and subsection 'P' at col 6? Actually
  // for procedure-type records the format is 'SUSAP <ICAO><region>D/E/F...'.
  // We match by the literal prefix to avoid edge cases.
  if (!line.startsWith('SUSAP ')) return false;
  const routeType = line[12];
  return routeType === 'D' || routeType === 'E' || routeType === 'F';
}

function isTerminalFix(line: string): boolean {
  // 'SUSAP <ICAO><region>C' — terminal waypoint (PC record)
  if (!line.startsWith('SUSAP ') || line.length < 13) return false;
  return line[12] === 'C';
}

function isRunway(line: string): boolean {
  // 'SUSAP <ICAO><region>G' — runway endpoint (PG record). Lat/lon
  // of the runway threshold is the MAP for an instrument approach
  // to that runway, so we MUST include these in the fix table.
  if (!line.startsWith('SUSAP ') || line.length < 13) return false;
  return line[12] === 'G';
}

function isEnrouteFix(line: string): boolean {
  return line.startsWith('SUSAEAENRT');
}

function isNavaidVor(line: string): boolean {
  return line.startsWith('SUSAD ');
}

function isNavaidNdb(line: string): boolean {
  return line.startsWith('SUSADB');
}

// ── Fix table ────────────────────────────────────────────────────────────

export interface FixRecord {
  ident: string;
  lat: number;
  lon: number;
  kind: 'terminal' | 'enroute' | 'vor' | 'ndb' | 'runway';
  /** Airport icao for terminal fixes + runway endpoints; undefined
   *  for global enroute / navaid records. */
  airportIcao?: string;
}

export interface FixTable {
  /** Terminal-fix entries are keyed by `airportIcao::ident` because the
   *  same 5-char ident can appear at multiple airports (terminal namespace).
   *  Enroute / VOR / NDB entries are keyed by `*::ident`. */
  byKey: Map<string, FixRecord>;
}

function parseFixLine(line: string): FixRecord | null {
  // Terminal fix (PC record):
  //   SUSAP <icao(4)>K?C<ident(5)> K? 0    W     N37361772W122201180...
  //   indexes:  0   6    12 13    18 19 22  24 25  26          33-51 (lat+lon)
  //
  // Enroute fix (EAENRT record):
  //   SUSAEAENRT   <ident(5)> K?0    W   B N32413827W078030466...
  //
  // VOR navaid:
  //   SUSAD <pad spaces><ident(3-5)> K?0...
  //
  // Lat starts at column 33 (0-indexed 32) for terminal/enroute fixes, but
  // it shifts for navaids. We extract the H{N|S} hemisphere marker by
  // scanning to handle both cases.

  if (line.length < 51) return null;

  // Find the H{N|S}{S}DDmmssHH{E|W}DDDmmssHH pattern. Lat is 9 chars,
  // lon is 10 chars, contiguous. Hemisphere chars (N/S then E/W) are
  // unambiguous so we scan for the first N|S followed by valid digits
  // and an E|W 9 chars later.
  for (let i = 26; i < Math.min(line.length - 18, 95); i++) {
    const c = line[i];
    if (c !== 'N' && c !== 'S') continue;
    const latStr = line.substring(i, i + 9);
    const lonStr = line.substring(i + 9, i + 19);
    if (!/^[NS]\d{8}$/.test(latStr) || !/^[EW]\d{9}$/.test(lonStr)) continue;
    const lat = decodeLat(latStr);
    const lon = decodeLon(lonStr);
    if (lat == null || lon == null) continue;

    if (isTerminalFix(line)) {
      const airportIcao = line.substring(6, 10).trim();
      const ident = line.substring(13, 18).trim();
      return { ident, lat, lon, kind: 'terminal', airportIcao };
    }
    if (isRunway(line)) {
      const airportIcao = line.substring(6, 10).trim();
      // Runway ident sits at the same cols as the terminal-fix ident
      // (cols 14-18, 0-indexed 13-18). It's 5 chars like 'RW29R'.
      const ident = line.substring(13, 18).trim();
      return { ident, lat, lon, kind: 'runway', airportIcao };
    }
    if (isEnrouteFix(line)) {
      const ident = line.substring(13, 18).trim();
      return { ident, lat, lon, kind: 'enroute' };
    }
    if (isNavaidVor(line)) {
      const ident = line.substring(13, 18).trim();
      return { ident, lat, lon, kind: 'vor' };
    }
    if (isNavaidNdb(line)) {
      const ident = line.substring(13, 18).trim();
      return { ident, lat, lon, kind: 'ndb' };
    }
    return null;
  }
  return null;
}

export function buildFixTable(lines: Iterable<string>): FixTable {
  const byKey = new Map<string, FixRecord>();
  for (const line of lines) {
    if (!line) continue;
    const rec = parseFixLine(line);
    if (!rec) continue;
    if ((rec.kind === 'terminal' || rec.kind === 'runway') && rec.airportIcao) {
      byKey.set(`${rec.airportIcao}::${rec.ident}`, rec);
    } else {
      // Enroute / navaid — global namespace. Use '*' for clarity.
      byKey.set(`*::${rec.ident}`, rec);
    }
  }
  return { byKey };
}

/** Resolve a fix used by a procedure leg. Prefer terminal-scoped match
 *  if the airport is given; fall back to the global table. */
export function resolveFix(
  table: FixTable,
  ident: string,
  airportIcao?: string,
): FixRecord | null {
  if (!ident) return null;
  if (airportIcao) {
    const terminal = table.byKey.get(`${airportIcao}::${ident}`);
    if (terminal) return terminal;
  }
  return table.byKey.get(`*::${ident}`) ?? null;
}

// ── Procedure record parser ──────────────────────────────────────────────

function parseProcedureLine(line: string): {
  airportIcao: string;
  routeType: RouteType;
  procIdent: string;
  transIdent: string;
  seq: number;
  fixIdent: string;
  fixRegion: string;
  legType: LegType;
  altitude1: string | null;
  altitude2: string | null;
  speedLimit: number | null;
  recNavaid: string | null;
  turnDirection: 'L' | 'R' | null;
  isMissedApproach: boolean;
  isHoldingPattern: boolean;
  holdInboundCourse: number | null;
  holdLeg: { kind: 'time'; minutes: number } | { kind: 'distance'; nm: number } | null;
  rfCenterFix: string | null;
  magCourse: number | null;
  isFaf: boolean;
  isMap: boolean;
  isIaf: boolean;
  isIntermediateFix: boolean;
  isStepdownFix: boolean;
  isFlyOver: boolean;
  altitudeConstraint: '+' | '-' | 'B' | 'at' | 'recommended' | null;
} | null {
  if (!isAirportProcedure(line)) return null;
  if (line.length < 132) return null;

  const airportIcao = line.substring(6, 10).trim();
  const routeChar = line[12];
  const routeType: RouteType = routeChar === 'D' ? 'SID' : routeChar === 'E' ? 'STAR' : 'IAP';
  const procIdent  = line.substring(13, 19).trim();
  const transIdent = line.substring(19, 24).trim();
  const seqStr     = line.substring(26, 29).trim();
  const seq        = Number(seqStr);
  if (!Number.isFinite(seq)) return null;
  const fixIdent  = line.substring(29, 34).trim();
  const fixRegion = line.substring(34, 36).trim();

  // Waypoint description codes — 4-char field starting at col 40 (0-indexed 39).
  // Positions verified empirically against captured CIFP cycle 260416
  // data. The role byte that flags FAF/IF/IAF/MAP/stepdown is at the
  // LAST position of the 4-char field (0-indexed 42, CIFP col 43):
  //   idx 39 (col 40): general type — 'E'=enroute fix, 'G'=runway, 'D'=navaid, 'H'=helo
  //   idx 40 (col 41): mostly unused on procedure rows (' ' typical)
  //   idx 41 (col 42): secondary flag — 'M'=missed-approach segment marker,
  //                    'S'=stepdown fix, 'Y'=fly-over, 'R'=runway transition end
  //   idx 42 (col 43): waypoint primary role — 'A'=IAF, 'I'=IF on approach,
  //                    'F'=FAF, 'M'=MAP, 'H'=holding fix, 'B'=back course
  // Examples:
  //   IPDEW (FAF on L29R):     '...0E  F...'
  //   SIMMS (IF on L29R):      '...0E  I...'
  //   WIGBI (stepdown L29R):   '...0E S ...'
  //   RW29R (MAP runway):      '...0GY M...'
  const wpdGeneral  = line[39]; // col 40
  const wpdSecondary = line[41]; // col 42
  const wpdRole      = line[42]; // col 43
  const isFaf   = wpdRole === 'F';
  const isMap   = wpdRole === 'M';
  const isIaf   = wpdRole === 'A';
  const isIntermediateFix = wpdRole === 'I';
  // Stepdown fix is marked at col 42.
  const isStepdownFix = wpdSecondary === 'S';
  // The MAP point itself + the missed-approach climb/turn fixes both
  // sit in the missed segment. CIFP marks: col 43='M' on the MAP fix,
  // col 42='M' on subsequent missed-approach legs.
  const isMissedApproach = isMap || wpdSecondary === 'M';
  // Holding-fix marker at col 43 OR hold-in-lieu marker at col 40.
  const isHoldingPattern = wpdRole === 'H' || wpdGeneral === 'H';
  // Fly-over fix marker at col 42.
  const isFlyOver = wpdSecondary === 'Y';

  // Altitude constraint type at col 83 (0-indexed 82). The char sits
  // right before alt1 in the record and indicates how alt1 should be
  // interpreted (+ = at or above, - = at or below, etc.).
  let altitudeConstraint: '+' | '-' | 'B' | 'at' | 'recommended' | null = null;
  if (line.length >= 83) {
    const c = line[82];
    if (c === '+' || c === '-' || c === 'B') altitudeConstraint = c;
    else if (c === '*') altitudeConstraint = 'recommended';
  }

  // Path-and-terminator (leg type) is at columns 48-49 (1-indexed)
  // → 0-indexed 47-49.
  const legTypeRaw = line.substring(47, 49).trim().toUpperCase();
  const legType: LegType = (
    ['IF','TF','DF','CF','RF','AF','VA','CA','FA','FC','FD','CD','CR','CI','VI','VM','VR','VD','PI','HM','HF','HA']
      .includes(legTypeRaw) ? legTypeRaw : 'UNKNOWN'
  ) as LegType;

  const turnRaw = line[43];
  const turnDirection: 'L' | 'R' | null =
    turnRaw === 'L' ? 'L' : turnRaw === 'R' ? 'R' : null;

  // Holding pattern fields — only meaningful on HM/HF/HA legs and PI
  // (procedure-turn) legs. Reading these columns on a TF/DF/CF would
  // pick up unrelated bytes since the column layout differs by leg
  // type. We restrict to known hold-bearing leg types.
  //   cols 71-74 (0-indexed 70-74) = inbound course, e.g. '2752' → 275.2°
  //   col 75 (0-indexed 74)        = 'T' = time-based, 'D' = distance-based
  //   cols 76-78 (0-indexed 75-78) = leg value scaled by 10 (e.g. '010' = 1.0 min)
  let holdInboundCourse: number | null = null;
  let holdLeg: { kind: 'time'; minutes: number } | { kind: 'distance'; nm: number } | null = null;
  const isHoldBearing = legType === 'HM' || legType === 'HF' || legType === 'HA' || legType === 'PI';
  if (isHoldBearing && line.length >= 78) {
    const crsRaw = line.substring(70, 74).trim();
    if (/^\d{4}$/.test(crsRaw)) {
      holdInboundCourse = Number(crsRaw) / 10;
    }
    const indicator = line[74];
    const legRaw = line.substring(75, 78).trim();
    if (/^\d{3}$/.test(legRaw)) {
      const value = Number(legRaw) / 10;
      if (indicator === 'T') holdLeg = { kind: 'time', minutes: value };
      else if (indicator === 'D') holdLeg = { kind: 'distance', nm: value };
    }
  }

  // RF (radius-to-fix) legs carry the center fix ident at cols 107-111
  // (0-indexed 106-111).
  let rfCenterFix: string | null = null;
  if (legType === 'RF' && line.length >= 111) {
    const c = line.substring(106, 111).trim();
    if (c.length > 0) rfCenterFix = c;
  }

  // Magnetic course/heading at cols 71-74 (0-indexed 70-74), scaled
  // by 10. Meaningful on CF (course-to-fix), VA (heading to altitude),
  // CA (course to altitude), FA (fix to altitude), FC (fix to distance),
  // and a few others. We extract for every leg type since the same
  // column is consistent across leg types.
  let magCourse: number | null = null;
  if (!isHoldBearing && line.length >= 74) {
    const courseRaw = line.substring(70, 74).trim();
    if (/^\d{4}$/.test(courseRaw)) {
      magCourse = Number(courseRaw) / 10;
    }
  }

  // Recommended NAVAID lives at col 51-54 (1-indexed) → 0-indexed 50-54.
  const recNavaidRaw = line.substring(50, 54).trim();
  const recNavaid = recNavaidRaw.length > 0 ? recNavaidRaw : null;

  // Altitude constraints. ARINC encodes alt1 at col 85-89 (1-indexed)
  // → 0-indexed 84-89, alt2 at 90-94 → 89-94. Both are 5-char fields
  // with optional 'FL' prefix or '+'/'-' constraint sign.
  const alt1Raw = line.substring(84, 89).trim();
  const alt2Raw = line.substring(89, 94).trim();
  const altitude1 = alt1Raw.length > 0 ? alt1Raw : null;
  const altitude2 = alt2Raw.length > 0 ? alt2Raw : null;
  // Resolve the altitude-constraint default after we know whether alt1
  // is present. No alt1 → null; alt1 with no prefix char → 'at'.
  if (!altitude1) altitudeConstraint = null;
  else if (altitudeConstraint == null) altitudeConstraint = 'at';

  // Speed limit at col 100-102 (1-indexed) → 0-indexed 99-102.
  const speedRaw = line.substring(99, 102).trim();
  const speedLimit = speedRaw && /^\d+$/.test(speedRaw) ? Number(speedRaw) : null;

  return {
    airportIcao, routeType, procIdent, transIdent, seq, fixIdent, fixRegion,
    legType, altitude1, altitude2, speedLimit, recNavaid, turnDirection,
    isMissedApproach, isHoldingPattern,
    isFaf, isMap, isIaf, isIntermediateFix,
    isStepdownFix, isFlyOver,
    altitudeConstraint,
    holdInboundCourse, holdLeg,
    rfCenterFix, magCourse,
  };
}

/** Build the full set of procedures for one or more airports.
 *
 * Strategy:
 *   1. Single pass: collect fix records into FixTable + raw procedure
 *      leg parses into a grouped map.
 *   2. Second pass over the grouped data: join fix coords, classify
 *      transitions, sort legs by seq, emit Procedure objects.
 */
export function parseProcedures(lines: Iterable<string>): Procedure[] {
  const fixTable = buildFixTable(lines);
  return parseProceduresWithFixes(lines, fixTable);
}

export function parseProceduresWithFixes(
  lines: Iterable<string>,
  fixTable: FixTable,
): Procedure[] {
  // Map<airport::route::procIdent, Map<transIdent, ProcedureLeg[]>>
  const grouped = new Map<string, Map<string, ProcedureLeg[]>>();
  const meta = new Map<string, { airportIcao: string; routeType: RouteType; procIdent: string }>();

  for (const line of lines) {
    const p = parseProcedureLine(line);
    if (!p) continue;

    // Skip continuation records (col 39 / 0-indexed 38 contains the
    // continuation number; 0 or 1 are primary, anything else is a
    // secondary record we don't need for path rendering).
    const cont = line[38];
    if (cont !== '0' && cont !== '1' && cont !== ' ') continue;

    const procKey = `${p.airportIcao}::${p.routeType}::${p.procIdent}`;
    meta.set(procKey, { airportIcao: p.airportIcao, routeType: p.routeType, procIdent: p.procIdent });
    let transMap = grouped.get(procKey);
    if (!transMap) {
      transMap = new Map();
      grouped.set(procKey, transMap);
    }
    let legs = transMap.get(p.transIdent);
    if (!legs) {
      legs = [];
      transMap.set(p.transIdent, legs);
    }

    const fix = resolveFix(fixTable, p.fixIdent, p.airportIcao);
    const rfCenter = p.rfCenterFix ? resolveFix(fixTable, p.rfCenterFix, p.airportIcao) : null;
    legs.push({
      seq: p.seq,
      legType: p.legType,
      fixIdent: p.fixIdent,
      fixLat: fix?.lat ?? null,
      fixLon: fix?.lon ?? null,
      altitude1: p.altitude1,
      altitude2: p.altitude2,
      speedLimit: p.speedLimit,
      recNavaid: p.recNavaid,
      turnDirection: p.turnDirection,
      isMissedApproach: p.isMissedApproach,
      isHoldingPattern: p.isHoldingPattern,
      isFaf: p.isFaf,
      isMap: p.isMap,
      isIaf: p.isIaf,
      isIntermediateFix: p.isIntermediateFix,
      isStepdownFix: p.isStepdownFix,
      isFlyOver: p.isFlyOver,
      altitudeConstraint: p.altitudeConstraint,
      holdInboundCourse: p.holdInboundCourse,
      holdLeg: p.holdLeg,
      rfCenterFix: p.rfCenterFix,
      rfCenterLat: rfCenter?.lat ?? null,
      rfCenterLon: rfCenter?.lon ?? null,
      magCourse: p.magCourse,
    });
  }

  const out: Procedure[] = [];
  for (const [procKey, transMap] of grouped) {
    const m = meta.get(procKey)!;
    const transitions: Transition[] = [];
    for (const [transIdent, legs] of transMap) {
      legs.sort((a, b) => a.seq - b.seq);
      transitions.push({
        ident: transIdent,
        kind: classifyTransitionKind(transIdent, m.routeType),
        legs,
      });
    }
    transitions.sort((a, b) => a.ident.localeCompare(b.ident));
    out.push({
      airportIcao: m.airportIcao,
      routeType: m.routeType,
      procIdent: m.procIdent,
      transitions,
    });
  }
  out.sort((a, b) =>
    a.airportIcao.localeCompare(b.airportIcao)
    || a.routeType.localeCompare(b.routeType)
    || a.procIdent.localeCompare(b.procIdent),
  );
  return out;
}

function classifyTransitionKind(transIdent: string, routeType: RouteType): Transition['kind'] {
  // Common segment: empty or 'ALL' or routeType-specific markers.
  if (!transIdent || transIdent === 'ALL') return 'common';
  // For IAPs, the common segment uses a single-letter ident matching
  // the approach type (I for ILS, R for RNAV/RNP common, H for high
  // RNP, L for LOC, V for VOR, N for NDB, etc.). Any single-char
  // non-digit transition ident on an IAP is the common segment.
  if (routeType === 'IAP' && transIdent.length === 1 && /^[A-Z]$/.test(transIdent)) {
    return 'common';
  }
  // Runway transitions are 'RW' + runway number, often prefixed with a
  // digit in CIFP (e.g. '4RW10L' on a SID). Strip the leading digit.
  const stripped = transIdent.replace(/^\d/, '');
  if (stripped.startsWith('RW')) return 'runway';
  return 'transition';
}
